import { Button, Card, CardHeader, useToast } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { saveSchedule, WEEKDAYS3, type StaffMember, type StaffSchedule, type WeeklySchedule, type Weekday3 } from "../../api/staff";
import { useServices } from "../../app/services";
import { FormError, messageOf, styles as c, useScope } from "../classes/common";
import s from "./staff.module.css";

const NAME: Record<Weekday3, string> = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const HM = /^([01]\d|2[0-3]):[0-5]\d$/;

export function ScheduleTab({ staff, schedule }: { staff: StaffMember; schedule: StaffSchedule | null }) {
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const canEdit = sc.ctx.has("staff.schedule");
  const [weekly, setWeekly] = useState<WeeklySchedule>(schedule?.weekly ?? {});
  const [grace, setGrace] = useState(String(schedule?.graceMinutes ?? 0));
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => saveSchedule(client, staff.id, { weekly, graceMinutes: Number(grace) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["staff"] }); void qc.invalidateQueries({ queryKey: ["attendance"] }); toast.show("Schedule saved"); },
    onError: (e) => setError(messageOf(e)),
  });
  const submit = () => {
    for (const d of WEEKDAYS3) {
      const w = weekly[d];
      if (w && (!HM.test(w.start) || !HM.test(w.end))) return setError(`${NAME[d]} needs a start and an end time.`);
      if (w && w.start === w.end) return setError(`${NAME[d]}: start and end must differ.`);
    }
    const g = Number(grace);
    if (!Number.isInteger(g) || g < 0 || g > 240) return setError("Grace minutes must be a whole number from 0 to 240.");
    setError(null);
    save.mutate();
  };
  const set = (d: Weekday3, v: { start: string; end: string } | null) => setWeekly({ ...weekly, [d]: v });
  return (
    <Card aria-label="Weekly schedule">
      <CardHeader title="Weekly schedule" subtitle="Days with no shift count as off; with no schedule at all, nobody is marked absent." />
      <form className={c.pad} onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate>
        {WEEKDAYS3.map((d) => {
          const w = weekly[d];
          return (
            <div key={d} className={s.sched}>
              <strong>{NAME[d]}</strong>
              <label className={c.check}><input type="checkbox" disabled={!canEdit} checked={!!w} onChange={(e) => set(d, e.target.checked ? { start: "09:00", end: "18:00" } : null)} aria-label={`${NAME[d]} works`} />Works</label>
              {w ? <input aria-label={`${NAME[d]} starts`} type="time" className={c.input} disabled={!canEdit} value={w.start} onChange={(e) => set(d, { ...w, start: e.target.value })} /> : <span className={c.note}>Off</span>}
              {w ? <input aria-label={`${NAME[d]} ends`} type="time" className={c.input} disabled={!canEdit} value={w.end} onChange={(e) => set(d, { ...w, end: e.target.value })} /> : null}
            </div>
          );
        })}
        <div className={c.stack}>
          <div className={c.field}><label htmlFor="grace">Grace minutes before a punch counts as late</label><input id="grace" className={c.input} inputMode="numeric" disabled={!canEdit} value={grace} onChange={(e) => setGrace(e.target.value)} /></div>
          <FormError message={error} />
          {canEdit ? <div><Button variant="primary" type="submit" busy={save.isPending}>Save schedule</Button></div> : null}
        </div>
      </form>
    </Card>
  );
}
